/* eslint-disable react/display-name */
import React, { useState, useEffect } from 'react';
import { useStoreActions } from '@models/RootStore';

import {
  Dialog,
  DialogContent,
  Typography,
  Collapse,
  LinearProgress
} from '@material-ui/core';
import { ExpandMore, ExpandLess, Close } from '@material-ui/icons';
import { t as translate } from 'ttag';
import clsx from 'clsx';
import { Button as ButtonV2, IconButton, LoadingText } from '@coolremote/shared';

import useStyles from './styles';

const AsyncJobProgressDialog = ({
  jobId,
  onClose,
  onComplete,
  selectedItems = []
 }) => {

  const classes = useStyles();

  const [job, setJob] = useState(null);
  const [errorCount, setErrorCount] = useState(0);
  const [isDetailsShown, setIsDetailsShown] = useState(false);
  const [isCloseDisabled, setIsCloseDisabled] = useState(true);

  const { addMessage } = useStoreActions((a) => a.errorMessage);
  const { getBackgroundJobById } = useStoreActions((a) => a.backgroundJobs);


  useEffect(() => {
    if (!jobId) { return; }

    const intervalRef = { current: null };

    const fetchJob = async () => {
      try {
        const data = await getBackgroundJobById(jobId);
        setJob(data);
        setErrorCount(0); // reset on success

        if (data.isCompleted) {
          setIsCloseDisabled(false);
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      } catch (err) {
        setErrorCount(prev => {
          const next = prev + 1;

          if (next >= 3 && intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
            setIsCloseDisabled(false);
          }

          return next;
        });
      }
    };

    const itemCount = selectedItems.length;
    let intervalTime = 3000;

    if (itemCount < 10) {
      intervalTime = 1000;
    } else if (itemCount < 50) {
      intervalTime = 2000;
    }

    fetchJob();
    intervalRef.current = setInterval(fetchJob, intervalTime);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [jobId, selectedItems.length]);

  const handleToggle = () => setIsDetailsShown(prev => !prev);

  const {
    status,
    total = 0,
    successCount = 0,
    failedCount = 0,
    isCompleted,
    items = []
  } = job || {};

  const progress = !job ? 0 : ((successCount + failedCount) / total) * 100;

  const handleClose = () => {
    onClose();
    if (jobId && onComplete) {
      onComplete();
    }
  }

  return (
    <Dialog open onClose={() => !isCloseDisabled && handleClose()}>
      {/* Dialog header/title */}
      <div className={classes.dialogHeader}>
        <Typography className={classes.headerTitle}>{translate`Applying Settings`}</Typography>
        <IconButton
          disableRipple
          aria-label="close"
          onClick={handleClose}
          disabled={isCloseDisabled}
          className={classes.iconBtnStyle}
        >
          <Close color="#7f7692" />
        </IconButton>
      </div>

      <DialogContent className={classes.dialogContent}>
        {
          !job ? <Typography>{translate`Preparing to apply settings...`}</Typography> :
          (
            <>
            <Typography style={{marginBottom: '10px'}}>
              {translate`Copying settings to selected units...`}
            </Typography>
            <div className={classes.LinearProgressContainer}>
              <div style={{width: '100%'}}>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  classes={{root: classes.progress}}
                />
              </div>
              <Typography className={classes.progressPercent}>{progress.toFixed(0)}%</Typography>
            </div>

            <Typography style={{marginBottom: '35px'}}>
              {successCount + failedCount} {translate`of`} {total} {translate`completed`}
              {!!failedCount &&
                <span className={classes.failedText}>
                 ( {failedCount} {translate`failed`} )
                </span>
              }
            </Typography>

            {errorCount >= 3 && (
              <Typography className={classes.errorText}>
                {translate`Polling progress stopped after 3 failed attempts. Please try again later.`}
              </Typography>
            )}

            {/* See More Toggle */}
            <div onClick={handleToggle} className={classes.detailsExpand}>
              <Typography>
                  {translate`You can view progress for each unit below`}
              </Typography>
              <IconButton size="small">
                {isDetailsShown ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </div>

            {/* Details List */}
            <Collapse in={isDetailsShown}>
              <div className={classes.resultsContainer}>
                {items.map((item, index) => (
                  <div className={clsx(classes.resultRow, { [classes.oddRow]: !(index % 2) })}>
                    <div className={clsx(classes.label, classes.nameCell)}>
                      {item.name}
                    </div>
                    <Typography
                      className={clsx(
                        classes.resultText,
                        {[classes.errorResult]: item.status.toLowerCase() === 'failed'}
                      )}
                    >
                      {item.status}
                    </Typography>
                  </div>
                ))
                }
              </div>
            </Collapse>
          </>
        )
      }
      </DialogContent>
      <div className={classes.actionsHolder}>
        <ButtonV2 disabled={isCloseDisabled} onClick={handleClose} variant="outlined">
          {translate`Close`}
        </ButtonV2>
      </div>
    </Dialog>
  );
};

export default AsyncJobProgressDialog;
